import {useCallback, useEffect, useState} from "react";
import {UpdateProfileForm} from "./UpdateForm";
import {FormActionButtons, FormattedFormRow, FormContainer, FormRowDivider, NumberField} from "@dvrt/graphql-widgets";
import {Checkbox, Table, TableBody, TableCell, TableHead, TableRow, Typography, styled} from "@mui/material";
import FormattedButton from "@/components/FormattedButton";
import {FormattedMessage, defineMessage} from "react-intl";
import {TextField} from "@dvrt/graphql-widgets";
import ActionRow from "@/components/ivr/profiles/ActionRow";
import {ActivationTimes} from "@/components/ActivationTimes";
import ProfileSpecialDayPicker from "@/components/ProfileSpecialDayPicker";
import {ActivationTime, ProfileSpecialDay} from "@/api";
import {createInitialData} from "./initialData";
import {
    ButtonPath,
    ButtonPathString,
    IvrProfile,
    MediaDownloadToken,
    UpdateButton,
    appendToButtonPathString,
    buttonPathStringToNumberArray,
    buttonPathToString,
} from "./tools";
import {DataSource} from "./DataSource";
import MediaTable from "./MediaTable";
import MenuDisplay from "./MenuDisplay";
import {Target} from "@/components/TargetSelector";
import {DeepPartial} from "@apollo/client/utilities";
import ErrorHelper from "@/utils/ErrorHelper";
import DialogMediaPlayer from "@/components/DialogMediaPlayer";
import useDialogManager from "@/hooks/useDialogManager";

export const TableBodyLastNoBorder = styled(TableBody)(() => ({
    "& tr:last-child td": {
        borderBottom: "none",
    },
}));

type ActionsProps = {
    currentPath: ButtonPath;
    buttons: [UpdateButton, number][];
    targets: Target[];
    openMediaPlayerDialog: (mediaDownloadToken: MediaDownloadToken) => void;
    submenuHasError: (nextButton: number) => boolean;
    update: (newButton: DeepPartial<UpdateButton>, frontendIndex: number) => void;
    goDeeper: (nextButton: number) => void;
    domainId: number;
    errorHelper: ErrorHelper;
};

const Actions = ({
    currentPath,
    buttons,
    targets,
    openMediaPlayerDialog,
    submenuHasError,
    update,
    goDeeper,
    domainId,
    errorHelper,
}: ActionsProps) => {
    return buttons.map(([button, frontendIndex], i) => (
        <ActionRow
            key={i}
            index={frontendIndex}
            button={button}
            targets={targets}
            disabled={false}
            isInSubmenu={currentPath.length > 0}
            openMediaPlayerDialog={openMediaPlayerDialog}
            submenuHasError={submenuHasError}
            update={(newButton) => update(newButton, frontendIndex)}
            goDeeper={goDeeper}
            domainId={domainId}
            targetErrors={errorHelper.fieldErrors("buttons", {
                contextComparator: (context) => context.path === currentPath.concat([i]).join(" -> "),
            })}
            connectionPromptErrors={errorHelper.fieldErrors(`buttons.${i}.connectionPrompt.file`)}
        />
    ));
};

type Props = {
    domainId: number;
    profileId: number;
    activate: () => void;
    isActive: boolean;
    otherProfiles: IvrProfile[];
    dataSource: DataSource;

    /// Whether the profile is currently being activated, so the form should be disabled for the time being
    activating: boolean;
};

const getIvrProfile = (ivr: DataSource["ivr"], profileId: number): IvrProfile =>
    ivr.profiles.find((p) => p.id === profileId);

const UpdateIvrProfileForm = ({
    domainId,
    profileId,
    activate,
    isActive,
    otherProfiles,
    dataSource,
    activating,
}: Props) => {
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [currentMenu, setCurrentMenu] = useState<ButtonPathString>("");
    const [notUniqueActivationTime, setNotUniqueValidationTime] = useState(false);
    const [notUniqueSpecificActivationTime, setNotUniqueSpecificActivationTime] = useState(false);
    const formDisabled = activating || submitting;
    const mediaPlayerDialog = useDialogManager<MediaDownloadToken>("");

    const initialData = useCallback(
        () => createInitialData(getIvrProfile(dataSource.ivr, profileId)),
        [dataSource, profileId],
    );

    const form = new UpdateProfileForm(
        profileId,
        domainId,
        initialData(),
        dataSource.rawTargets.ivrs.map((ivr) => ivr.number.id),
    );

    const errorHelper = new ErrorHelper(form.errors);

    useEffect(() => {
        console.log("form errors", form.errors);
    }, [form.errors]);

    const closeMediaPlayerDialog = useCallback(() => {
        mediaPlayerDialog.updateState({isOpen: false, data: undefined});
    }, [mediaPlayerDialog]);

    const openMediaPlayerDialog = useCallback(
        (mediaDownloadToken: MediaDownloadToken) => {
            mediaPlayerDialog.updateState({isOpen: true, data: mediaDownloadToken});
        },
        [mediaPlayerDialog],
    );

    const submit = async () => {
        setSubmitting(true);
        if (await form.submit()) {
            const newData = await dataSource.refetch();
            form.newInitialData(createInitialData(getIvrProfile(newData, profileId)));
        }
        setSubmitting(false);
    };

    const goDeeper = (button: number) => {
        setCurrentMenu(appendToButtonPathString(currentMenu, button));
    };

    const checkActivationTimeIsUnique = (newActivationTimes: ActivationTime[]) => {
        if (!newActivationTimes.length) {
            setNotUniqueValidationTime(false);
        }

        const otherActivationTimes = otherProfiles.flatMap((profile) => profile.activationTimes);

        const hasConflict = newActivationTimes.some((group) => {
            return group.weekdays.some((wd) => {
                return otherActivationTimes.some((other) => {
                    return other.weekday == wd && group.times.some((t) => t == other.activationTime);
                });
            });
        });

        setNotUniqueValidationTime(hasConflict);
    };

    const datesMonthAndDayAreEqual = (dateOne: string, dateTwo: string): boolean => {
        return dateOne.split("-")[1] == dateTwo.split("-")[1] && dateOne.split("-")[2] == dateTwo.split("-")[2];
    };

    const checkSpecificActivationTimeIsUnique = (newActivationTimes: ProfileSpecialDay[]) => {
        if (!newActivationTimes.length) {
            setNotUniqueSpecificActivationTime(false);
        }

        const otherSpecialActivationTimes = otherProfiles.flatMap((profile) => profile.specialDays);

        const hasConflict = newActivationTimes.some((newActivationTime) => {
            const otherProfileTime = otherSpecialActivationTimes.find((otherProfileTime) => {
                if (otherProfileTime.everyYear || newActivationTime.everyYear) {
                    return (
                        datesMonthAndDayAreEqual(otherProfileTime.day, newActivationTime.day) &&
                        otherProfileTime.activationTime == newActivationTime.activationTime
                    );
                } else {
                    return (
                        otherProfileTime.day == newActivationTime.day &&
                        otherProfileTime.activationTime == newActivationTime.activationTime
                    );
                }
            });

            return !!otherProfileTime;
        });

        setNotUniqueSpecificActivationTime(hasConflict);
    };

    return (
        <>
            <div style={{marginBottom: 20, marginTop: 10}}>
                <FormContainer>
                    <FormattedFormRow>
                        <FormattedButton
                            message={defineMessage({id: "ivr.profile_activation", defaultMessage: "Activate profile"})}
                            size="small"
                            onClick={activate}
                            disabled={isActive || formDisabled}
                            pending={activating}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "displayName", defaultMessage: "Name"})}>
                        <TextField
                            value={form.values.name}
                            onChange={(value) => form.updateValues({name: value})}
                            disabled={formDisabled}
                        />
                    </FormattedFormRow>
                    <FormRowDivider />
                    <FormattedFormRow label={defineMessage({id: "stickyProfile", defaultMessage: "Sticky profile"})}>
                        <Checkbox
                            checked={form.values.settings.stickyProfile}
                            onChange={(_) =>
                                form.updateValues({settings: {stickyProfile: !form.values.settings.stickyProfile}})
                            }
                            disabled={formDisabled}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({id: "activationTime.label", defaultMessage: "Activation times"})}
                    >
                        <ActivationTimes
                            activationTimes={form.values.settings.activationTimes}
                            disabled={formDisabled}
                            updater={(newActivationTimes) =>
                                form.updateValues({settings: {activationTimes: newActivationTimes.activationTimes}})
                            }
                            uniqueValidator={checkActivationTimeIsUnique}
                        />
                        {notUniqueActivationTime && (
                            <Typography
                                variant="subtitle2"
                                style={{flex: 1, cursor: "default", color: "red", textAlign: "right"}}
                            >
                                <FormattedMessage
                                    id="ivr.activationTimes.notUnique"
                                    defaultMessage="The added activation time is already added to another profile"
                                />
                            </Typography>
                        )}
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({
                            id: "specialDays.profile.label",
                            defaultMessage: "Specific activation times",
                        })}
                    >
                        <ProfileSpecialDayPicker
                            specialDays={form.values.settings.specialDays}
                            disabled={formDisabled}
                            updater={(newActivationTimes) =>
                                form.updateValues({settings: {specialDays: newActivationTimes}})
                            }
                            uniqueValidator={checkSpecificActivationTimeIsUnique}
                        />
                        {notUniqueSpecificActivationTime && (
                            <Typography
                                variant="subtitle2"
                                style={{flex: 1, cursor: "default", color: "red", textAlign: "right"}}
                            >
                                <FormattedMessage
                                    id="specialDays.profile.notUnique"
                                    defaultMessage="The added specific activation time is already added to another profile"
                                />
                            </Typography>
                        )}
                    </FormattedFormRow>
                    <FormRowDivider verticalPadding={25} />
                    <FormattedFormRow
                        label={defineMessage({id: "ivr.greetings", defaultMessage: "Greetings"})}
                        labelAlign="top"
                    >
                        <MediaTable
                            domainId={domainId}
                            media={form.getMenuMedia("", "greetings")}
                            update={(newGreetings) => form.updateMenuMedia("", newGreetings, "greetings")}
                            disabled={formDisabled}
                            getErrorsForIndex={(i) => errorHelper.fieldErrors(`greetings.${i}.file`)}
                            openMediaPlayerDialog={openMediaPlayerDialog}
                        />
                    </FormattedFormRow>
                    <FormRowDivider verticalPadding={25} />
                </FormContainer>
                <FormContainer sx={{rowGap: 4}}>
                    <FormattedFormRow
                        label={defineMessage({id: "ivr.currentMenu", defaultMessage: "Current menu"})}
                        labelStyle={{paddingTop: null, alignSelf: "center"}}
                    >
                        <MenuDisplay
                            path={buttonPathStringToNumberArray(currentMenu)}
                            newPathCallback={(newPath) => setCurrentMenu(buttonPathToString(newPath))}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({
                            id: "ivr.can_be_dialled_immediately",
                            defaultMessage: "Users can be dialled immediately",
                        })}
                    >
                        <Checkbox
                            checked={form.getCrossPathField(currentMenu, "instantConnect")}
                            onChange={(_, newValue) => form.setCrossPathField(currentMenu, "instantConnect", newValue)}
                            disabled={formDisabled}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({
                            id: "ivr.timeBeforeDefaultAction",
                            defaultMessage: "Timeout before default action",
                        })}
                    >
                        <NumberField
                            variant="outlined"
                            margin="dense"
                            value={form.getCrossPathField(currentMenu, "timeout")}
                            update={(value) => form.setCrossPathField(currentMenu, "timeout", value)}
                            disabled={formDisabled}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({id: "ivr.menu", defaultMessage: "Menu media"})}
                        labelAlign="top"
                    >
                        <MediaTable
                            domainId={domainId}
                            media={form.getMenuMedia(currentMenu, "menuMedia")}
                            update={(newMenuMedia) => form.updateMenuMedia(currentMenu, newMenuMedia, "menuMedia")}
                            disabled={formDisabled}
                            getErrorsForIndex={(i) => {
                                if (currentMenu === "") {
                                    console.log("getting errors for", `menuMedia.${i}.file`);
                                    return errorHelper.fieldErrors(`menuMedia.${i}.file`);
                                }
                                return errorHelper.fieldErrors(
                                    `submenus.${form.getSubmenuIndexForPath(currentMenu)}.menuMedia.${i}.file`,
                                );
                            }}
                            openMediaPlayerDialog={openMediaPlayerDialog}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({id: "ivr.actions", defaultMessage: "Actions"})}
                        labelAlign="top"
                    >
                        <Table size="medium">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <FormattedMessage id="ivr.action-headers.key" defaultMessage="Key" />
                                    </TableCell>
                                    <TableCell>
                                        <FormattedMessage id="ivr.action-headers.action" defaultMessage="Action" />
                                    </TableCell>
                                    <TableCell>
                                        <FormattedMessage id="ivr.action-headers.target" defaultMessage="Target" />
                                    </TableCell>
                                    <TableCell>
                                        <FormattedMessage
                                            id="ivr.action-headers.connection-prompt"
                                            defaultMessage="Connection prompt"
                                        />
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBodyLastNoBorder>
                                <Actions
                                    currentPath={buttonPathStringToNumberArray(currentMenu)}
                                    buttons={form.getButtonsForFrontend(currentMenu)}
                                    targets={dataSource.targets}
                                    submenuHasError={(nextButton) =>
                                        form.submenuHasError(appendToButtonPathString(currentMenu, nextButton))
                                    }
                                    update={(newButton, frontendIndex) =>
                                        form.updateButtonFor(currentMenu, frontendIndex, newButton)
                                    }
                                    goDeeper={goDeeper}
                                    domainId={domainId}
                                    errorHelper={errorHelper}
                                    openMediaPlayerDialog={openMediaPlayerDialog}
                                />
                            </TableBodyLastNoBorder>
                        </Table>
                    </FormattedFormRow>
                    <FormRowDivider />
                    <FormattedFormRow>
                        <div style={{margin: "1em 0"}}>
                            <FormActionButtons
                                mainAction={submit}
                                secondaryAction={() => {
                                    if (currentMenu !== "") setCurrentMenu("");
                                    form.reset();
                                }}
                                mainButtonEnabled={
                                    form.hasChanged &&
                                    !formDisabled &&
                                    !notUniqueActivationTime &&
                                    !notUniqueSpecificActivationTime
                                }
                                secondaryButtonEnabled={form.hasChanged && !formDisabled}
                            />
                        </div>
                    </FormattedFormRow>
                </FormContainer>
            </div>
            <DialogMediaPlayer
                open={mediaPlayerDialog.isOpen}
                onClose={closeMediaPlayerDialog}
                mediaDownloadToken={mediaPlayerDialog.data}
            />
        </>
    );
};

export default UpdateIvrProfileForm;
